'use client';

import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { OptimizelyProvider, type ReactSDKClient } from '@optimizely/react-sdk';
import { getConfig } from '@fshr/shared-react/config';
import { useOneTrust } from '@fshr/hooks';
import { deleteUserId, getOrCreateUserId } from './user-id';
import { getOrCreateClient } from './client-singleton';
import { ExperimentsErrorBoundary } from './experiments-error-boundary';
import { useExperimentAdobeForwarding } from '../hooks/use-experiment-adobe-forwarding';

import type { UserAttributes } from '@optimizely/optimizely-sdk';

// Runs the Adobe forwarding inside the Optimizely context, so every consumer gets it.
function AdobeForwardingBridge() {
	useExperimentAdobeForwarding();
	return null;
}

type ExperimentsProviderProps = PropsWithChildren<{
	timeout?: number;
	userId?: string;
	attributes?: UserAttributes;
}>;

function ExperimentsProviderInner({
	children,
	timeout = 5000,
	attributes = {},
	userId: userIdOverride,
}: ExperimentsProviderProps) {
	const { FSHR_PUBLIC_OPTIMIZELY_SDK_KEY } = getConfig();
	const sdkKey = String(FSHR_PUBLIC_OPTIMIZELY_SDK_KEY ?? '');

	const { oneTrustLoaded, cookieCategories } = useOneTrust();
	const performanceAllowed = oneTrustLoaded && cookieCategories.performance;

	// Only initialize the Optimizely client once performance cookies are allowed.
	const clientRef = useRef<ReactSDKClient | null>(null);
	if (performanceAllowed && clientRef.current === null) {
		try {
			clientRef.current = getOrCreateClient(sdkKey);
		} catch (e) {
			console.error('[ExperimentsProvider] getOrCreateClient failed:', e);
		}
	}

	// userId starts as null — no cookie is written until performance consent is confirmed.
	const [userId, setUserId] = useState<string | null>(null);

	useEffect(() => {
		if (!performanceAllowed) return;
		const nextUserId = userIdOverride ?? getOrCreateUserId();
		setUserId(current => (nextUserId === current ? current : nextUserId));
	}, [userIdOverride, performanceAllowed]);

	useEffect(() => {
		if (oneTrustLoaded && !cookieCategories.performance) {
			deleteUserId();
		}
	}, [oneTrustLoaded, cookieCategories.performance]);

	if (!performanceAllowed || !clientRef.current || !userId) {
		return <>{children}</>;
	}

	return (
		<OptimizelyProvider
			optimizely={clientRef.current}
			user={{ id: userId, attributes }}
			timeout={timeout}
		>
			<AdobeForwardingBridge />
			{children}
		</OptimizelyProvider>
	);
}

export function ExperimentsProvider(props: ExperimentsProviderProps) {
	return (
		<ExperimentsErrorBoundary fallback={<>{props.children}</>}>
			<ExperimentsProviderInner {...props} />
		</ExperimentsErrorBoundary>
	);
}
